<template>
  <div
    :data-test-id="isCardRef ? 'message-card' : 'message'"
    :class="[
      'message',
      {
        'message--own': isOwnRef,
        'message--card': isCardRef,
        'message--grouped-prev': groupedWithPrevious,
        'message--grouped-next': groupedWithNext,
      },
    ]"
  >
    <div class="message__content">
      <Avatar class="message__content__avatar">
        <AvatarImage v-if="senderPictureRef" :src="senderPictureRef" alt="" />
        <AvatarFallback class="text-[0.6875rem] font-semibold uppercase">{{ senderInitialsRef }}</AvatarFallback>
      </Avatar>
      <!-- bulle de texte, puis images et fichiers sous la bulle (maquette) -->
      <div class="message__content__body">
        <!-- carte (session) a la place de la bulle : meme auteur, meme alignement -->
        <slot v-if="isCardRef" name="body"></slot>
        <template v-else>
          <div
            v-if="hasTextRef"
            class="message__content__text"
            data-test-id="message-bubble"
            :title="groupedWithNext ? sentAtTimeRef : undefined"
          >
            <div v-html="formatMessage(message.content)"></div>
          </div>
          <MessageImageGrid v-if="imagesRef.length > 0" :images="imagesRef" @open="openLightbox" />
          <div v-if="otherFilesRef.length > 0" data-test-id="message-files-block" class="message__content__files">
            <MessageAttachment
              v-for="file in otherFilesRef"
              :key="file.id"
              :file="file"
              @open="handleFileClick(file)"
              @download="handleFileDownload(file)"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- dans un bloc, l'heure de chaque message reste disponible (infobulle, lecteur d'ecran) -->
    <time v-if="groupedWithNext" class="sr-only" :datetime="message.sentAt">{{ sentAtTimeRef }}</time>
    <div v-else class="message__sub-info">
      <div class="message__sub-info__info">
        <template v-if="userRef">
          <span> {{ userRef.firstName }} {{ userRef.lastName }}</span
          ><span>·</span><span v-for="role in groupRolesRef" :key="role">{{ t(userRolesNamesMap[role]) }}</span>
        </template>
        <template v-else>
          <span>{{ t('ui.message.loading') }}</span>
        </template>
      </div>
      <time class="message__sub-info__time" :datetime="message.sentAt">{{ sentAtRelativeTimeRef }}</time>
    </div>

    <!-- eslint-disable vue/no-v-model-argument -->
    <ImageLightbox
      v-if="imagesRef.length > 0"
      :images="imagesRef"
      v-model:open="lightboxOpenRef"
      v-model:index="lightboxIndexRef"
    />
    <!-- eslint-enable vue/no-v-model-argument -->
  </div>
</template>

<script setup lang="ts">
import DOMPurify from 'dompurify'
import linkifyHtml from 'linkify-html'
import { computed, ref, useSlots } from 'vue'

import ImageLightbox from './image-lightbox.vue'
import MessageAttachment from './message-attachment.vue'
import MessageImageGrid from './message-image-grid.vue'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import useAccount from '@/features/auth/hooks/use-account.hook'
import type { V2MessageType } from '@/features/chat/types'
import { downloadChatFile, openChatFile } from '@/features/chat/utils/open-chat-file'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import useUser from '@/features/users/hooks/use-user.hook'
import type { V2UserDataType } from '@/features/users/types'
import { userRolesNamesMap } from '@/features/users/utils'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'
import { PROFILE_PICTURE_BUCKET_URL } from '@/utils/config/constants'
import { getRelativeTimeToDate } from '@/utils/dates'

const { t, locale } = useI18n()

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif']

const props = defineProps<{
  message: V2MessageType
  groupId: string
  conversationUsers: V2UserDataType[]
  // bloc de messages du meme auteur (voir message-grouping.ts)
  groupedWithPrevious?: boolean
  groupedWithNext?: boolean
}>()

const { accountRef } = useAccount()
const slots = useSlots()
// message porteur d'une carte (session) : pas de bulle, la carte prend sa place
const isCardRef = computed(() => !!slots.body)

const messageUserIdRef = computed(() => props.message.userId)
const groupIdRef = computed(() => props.groupId)
const { userRef } = useUser(messageUserIdRef)
const messageSenderRolesRef = computed(() => {
  if (props.conversationUsers) {
    const senderUser = props.conversationUsers.find((user) => user.id === props.message.userId)
    return senderUser?.roles || []
  }
  return []
})

const { groupRolesRef: fallbackGroupRolesRef } = useGroupRoles({ groupIdRef })
const groupRolesRef = computed(() =>
  messageSenderRolesRef.value.length > 0 ? messageSenderRolesRef.value : fallbackGroupRolesRef.value || [],
)
const sentAtRelativeTimeRef = computed(() => getRelativeTimeToDate(props.message.sentAt))
// heure seule, formats en dur par langue (les traductions prennent une majuscule)
const sentAtTimeRef = computed(() =>
  dayjsClient(props.message.sentAt).format(String(locale.value).startsWith('fr') ? 'HH:mm' : 'h:mm A'),
)
const isOwnRef = computed(() => props.message.userId === accountRef.value?.id)
const senderInitialsRef = computed(() => `${userRef.value?.firstName?.[0] ?? ''}${userRef.value?.lastName?.[0] ?? ''}`)
const senderPictureRef = computed(() =>
  userRef.value?.picture ? PROFILE_PICTURE_BUCKET_URL + userRef.value.picture : undefined,
)

function isImageFile(file: { ext: string; signedUrl?: string }): boolean {
  return !!file.signedUrl && IMAGE_EXTENSIONS.includes((file.ext || '').toLowerCase())
}

const imagesRef = computed(() => (props.message.files ?? []).filter(isImageFile))
// un message avec seulement des fichiers n'a pas de bulle vide
const hasTextRef = computed(() => props.message.content.trim().length > 0)
const otherFilesRef = computed(() => (props.message.files ?? []).filter((f) => !isImageFile(f)))

const lightboxOpenRef = ref(false)
const lightboxIndexRef = ref(0)

function openLightbox(index: number) {
  lightboxIndexRef.value = index
  lightboxOpenRef.value = true
}

function formatMessage(message: string) {
  const mentionRegex = /@([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/gi
  let formattedMessage = message.replace(mentionRegex, (match, userId) => {
    const mentionedUser = props.conversationUsers.find((user) => user.id === userId)
    if (mentionedUser) {
      const userName = `${mentionedUser.firstName || ''} ${mentionedUser.lastName || ''}`.trim() || 'Utilisateur'
      return `<span class="mention">@${userName}</span>`
    }
    return match
  })

  formattedMessage = linkifyHtml(DOMPurify.sanitize(formattedMessage), {
    defaultProtocol: 'https',
    attributes: {
      target: '_blank',
      rel: 'noopener',
    },
  })
  return formattedMessage.trim()
}

function handleFileClick(file: { id: string; name: string; ext: string }) {
  return openChatFile(file, props.message.userId)
}

function handleFileDownload(file: { id: string; name: string }) {
  return downloadChatFile(file, props.message.userId)
}
</script>

<style lang="scss">
@import './message.scss';
</style>
